import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { fmtMoneyK, fmtDate } from './UI';

// Global "jump to anything" search. Press / or click the box to focus,
// type 2+ chars, get a dropdown of matching accounts and projects.
// Pressing Enter on a result navigates there.
export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState({ accounts: [], projects: [] });
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Keyboard: "/" focuses search (unless typing in another input)
  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Debounced search
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults({ accounts: [], projects: [] });
      return;
    }
    const t = setTimeout(() => {
      api.search(q).then(setResults).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const items = [
    ...results.accounts.map(name => ({ kind: 'account', name, key: 'a:' + name })),
    ...results.projects.map(p => ({ kind: 'project', project: p, key: 'p:' + p.id }))
  ];

  const choose = (item) => {
    if (item.kind === 'account') nav(`/accounts/${encodeURIComponent(item.name)}`);
    else nav('/projects');
    setOpen(false);
    setQ('');
    inputRef.current?.blur();
  };

  const onKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, items.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    if (e.key === 'Enter' && items[highlight]) { e.preventDefault(); choose(items[highlight]); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={q}
        onChange={e => { setQ(e.target.value); setHighlight(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKey}
        placeholder="🔍  Search... ( / )"
        style={{
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          color: '#fff', padding: '6px 12px', borderRadius: 6,
          fontSize: 13, width: 260, fontFamily: 'inherit'
        }}
      />
      {open && q.trim().length >= 2 && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
          background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6,
          minWidth: 380, boxShadow: '0 8px 24px rgba(0,0,0,0.18)', zIndex: 50,
          maxHeight: 480, overflow: 'auto'
        }}>
          {items.length === 0 && (
            <div style={{ padding: 12, color: '#6B7280', fontSize: 13 }}>No matches.</div>
          )}
          {results.accounts.length > 0 && (
            <>
              <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #F3F4F6' }}>
                Accounts
              </div>
              {results.accounts.map((name, i) => {
                const idx = i;
                return (
                  <div
                    key={name}
                    onMouseDown={() => choose({ kind: 'account', name })}
                    onMouseEnter={() => setHighlight(idx)}
                    style={{
                      padding: '8px 12px', fontSize: 14, cursor: 'pointer',
                      background: highlight === idx ? '#FEF3C7' : 'transparent'
                    }}
                  >
                    🏢 {name}
                  </div>
                );
              })}
            </>
          )}
          {results.projects.length > 0 && (
            <>
              <div style={{ padding: '6px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid #F3F4F6', borderTop: '1px solid #F3F4F6' }}>
                Projects
              </div>
              {results.projects.map((p, i) => {
                const idx = results.accounts.length + i;
                return (
                  <div
                    key={p.id}
                    onMouseDown={() => choose({ kind: 'project', project: p })}
                    onMouseEnter={() => setHighlight(idx)}
                    style={{
                      padding: '8px 12px', fontSize: 14, cursor: 'pointer',
                      background: highlight === idx ? '#FEF3C7' : 'transparent'
                    }}
                  >
                    📋 {p.site} <span style={{ color: '#6B7280', fontSize: 12 }}>· {p.status} · {fmtMoneyK(p.estimated_value)} · {fmtDate(p.date_planned)}</span>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
